import { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import './Pages.css';

/* Each stage shows a real location in Nepal so it feels concrete */
const stages = [
  {
    label: 'SOS Button',
    sub: 'Remote trekker presses',
    x: 30, y: 36,
    place: 'Langtang Valley',
    pinColor: '#d8232a',
    lines: [
      'Physical SOS device',
      'One button, no menus',
      'GPS + ID included',
    ],
    bodyText: 'A hiker or villager presses a single physical button. The device captures GPS coordinates, a timestamp, and the device identity, then starts transmitting.',
  },
  {
    label: 'LoRa Transmission',
    sub: 'Radio wave travels',
    x: 55, y: 32,
    place: 'Across mountains',
    pinColor: '#d8232a',
    lines: [
      'Sub-GHz frequency',
      'No SIM card needed',
      'Works without towers',
    ],
    bodyText: 'The alert is sent as a long-range, low-power radio packet. It travels through valleys and ridges where mobile phones have no coverage, with no cellular subscription required.',
  },
  {
    label: 'Gateway / Receiver',
    sub: 'Solar receiver picks it up',
    x: 75, y: 45,
    place: 'Besisahar Gateway',
    pinColor: '#1a2849',
    lines: [
      'Buffers during outages',
      'Solar + battery powered',
      'Forwards via internet',
    ],
    bodyText: 'A gateway on a nearby ridge or building receives the LoRa packet. Even if internet backhaul drops, it buffers the alert and sends it the moment the connection returns.',
  },
  {
    label: 'Dashboard Live',
    sub: 'Responder sees it instantly',
    x: 42, y: 68,
    place: 'Kathmandu Response',
    pinColor: '#1a2849',
    lines: [
      'Real-time alert card',
      'Ward-level filtering',
      'Status tracking (5 states)',
    ],
    bodyText: 'The alert appears on the web dashboard with an exact map pin. A responder can acknowledge, assign, track progress, and resolve, turning minutes into saved lives.',
  },
];

export default function Flow() {
  const [active, setActive] = useState(0);
  const activeStage = stages[active];

  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % stages.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="page">
      <section className="page-hero page-hero--dark" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <span className="eyebrow">How the signal travels</span>
          <h1>From a single press in the mountains to a live response board.</h1>
          <p className="lede">Follow the path step by step. Click any stage on the map to jump.</p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 48 }}>
        <div className="container" style={{ maxWidth: 1160 }}>
          {/* Main visual: Nepal map with journey */}
          <div style={{
            position: 'relative',
            background: '#f0f2f5',
            borderRadius: 24,
            border: '1px solid rgba(26,40,73,0.08)',
            overflow: 'hidden',
            padding: 0,
          }}>
            {/* Header banner inside the map card */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 28px',
              background: '#1a2849',
              color: '#fff',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: '#ef7a7f', fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  LIVE PATH, Step {active + 1} of {stages.length}
                </span>
                <span style={{ color: '#8a96bf', fontSize: '0.8rem' }}>/</span>
                <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{activeStage.label}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {stages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    aria-label={`Go to step ${i + 1}`}
                    style={{
                      width: 10, height: 10,
                      borderRadius: '50%',
                      border: 'none',
                      background: i === active ? '#d8232a' : 'rgba(255,255,255,0.18)',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* The actual visual map */}
            <div style={{ position: 'relative', height: 440, overflow: 'hidden' }}>
              {/* Stylized Nepal outline SVG as background */}
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid meet"
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.9,
                }}
                aria-hidden
              >
                <defs>
                  <linearGradient id="mapGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#e8ecf2" />
                    <stop offset="100%" stopColor="#c8cdd8" />
                  </linearGradient>
                </defs>
                <rect width="100" height="100" fill="url(#mapGrad)" />
                {/* Simplified Nepal silhouette */}
                <path
                  d="M 22 16 L 38 11 L 55 13 L 70 21 L 80 31 L 74 42 L 82 49 L 76 58 L 68 68 L 60 80 L 48 92 L 38 86 L 30 78 L 22 66 L 18 54 L 16 42 L 20 30 Z"
                  fill="#e0e6f0"
                  stroke="#1a2849"
                  strokeWidth="0.6"
                  strokeOpacity="0.3"
                />
                {/* Mountain ridges as decorative lines */}
                <path d="M 32 24 L 48 18 L 60 26 L 72 20 L 78 30" fill="none" stroke="#1a2849" strokeWidth="0.3" strokeOpacity="0.15" />
                <path d="M 30 50 L 50 42 L 65 55 L 75 48" fill="none" stroke="#1a2849" strokeWidth="0.3" strokeOpacity="0.15" />
                <path d="M 22 72 L 42 65 L 58 75 L 70 68" fill="none" stroke="#1a2849" strokeWidth="0.3" strokeOpacity="0.15" />
                {/* City dots for reference */}
                <circle cx="55" cy="42" r="1.2" fill="#1a2849" opacity="0.5" />
                <circle cx="65" cy="62" r="0.9" fill="#1a2849" opacity="0.5" />
              </svg>

              {/* Journey path line */}
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
                aria-hidden
              >
                {/* Animated curved line connecting the stages */}
                <path
                  d="M 30 36 C 45 28, 50 32, 55 32 C 60 32, 65 35, 75 45 C 82 52, 65 55, 55 52 C 45 50, 35 58, 42 68"
                  fill="none"
                  stroke="#d8232a"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray="8 4"
                  opacity="0.9"
                />
                {/* Animated travel dot */}
                <circle r="3.5" fill="#d8232a" stroke="#fff" strokeWidth="1.5">
                  <animateMotion
                    dur="4s"
                    repeatCount="indefinite"
                    path="M 30 36 C 45 28, 50 32, 55 32 C 60 32, 65 35, 75 45 C 82 52, 65 55, 55 52 C 45 50, 35 58, 42 68"
                    calcMode="spline"
                    keyTimes="0;0.25;0.5;0.75;1"
                    keySplines="0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1;0.4 0 0.6 1"
                  />
                </circle>
              </svg>

              {/* The 4 stage pins */}
              {stages.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => setActive(i)}
                  aria-label={`Jump to ${s.label}`}
                  style={{
                    position: 'absolute',
                    left: `calc(${s.x}% - 20px)`,
                    top: `calc(${s.y}% - 20px)`,
                    width: 40, height: 40,
                    border: 'none', borderRadius: '50%',
                    background: s.pinColor,
                    boxShadow: `0 4px 14px ${s.pinColor}55, 0 0 0 3px #fff`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', cursor: 'pointer',
                    zIndex: 3,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    transform: i === active ? 'scale(1.25)' : 'scale(1)',
                    animation: i === active ? 'pulse-dot 2s infinite ease' : 'none',
                  }}
                  title={s.place}
                >
                  <Icon name={['alert', 'lora', 'gateway', 'map'][i] as any} size={18} />
                </button>
              ))}

              {/* Place labels near each pin */}
              {stages.map((s, i) => (
                <div key={`label-${i}`} style={{
                  position: 'absolute',
                  left: `calc(${s.x}% + ${i === 2 ? -10 : 20}px)`,
                  top: `calc(${s.y}% + ${i === 3 ? 30 : -30}px)`,
                  background: '#1a2849',
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 9999,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 4,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  border: `1.5px solid ${s.pinColor}`,
                }}>
                  {s.place}
                </div>
              ))}
            </div>
          </div>

          {/* Story explanation panel */}
          <div style={{
            marginTop: 32,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 28,
          }}>
            {/* Left: the stage card with icons */}
            <div style={{
              background: '#fff',
              borderRadius: 20,
              border: '1px solid rgba(26,40,73,0.08)',
              padding: 32,
              boxShadow: '0 8px 30px rgba(26,40,73,0.05)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{
                  width: 14, height: 14, borderRadius: '50%', background: activeStage.pinColor,
                }} />
                <span style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', color: activeStage.pinColor, textTransform: 'uppercase' }}>
                  STEP {active + 1}
                </span>
              </div>
              <h2 style={{ fontSize: '1.6rem', marginBottom: 6, color: '#1a2849' }}>{activeStage.label}</h2>
              <p style={{ fontSize: '0.85rem', color: '#6e6e6a', marginBottom: 24 }}>{activeStage.sub}, {activeStage.place}</p>
              <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {activeStage.lines.map((line) => (
                  <li key={line} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ color: activeStage.pinColor, fontWeight: 900, fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: '0.95rem', color: '#3a3a37', lineHeight: 1.4 }}>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: plain-language explanation (so anyone can understand) */}
            <div style={{
              background: '#1a2849',
              borderRadius: 20,
              padding: 32,
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: 14, color: '#fff', fontFamily: 'var(--font-display)' }}>
                What is happening here?
              </h3>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.9)' }}>
                {activeStage.bodyText}
              </p>
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.12)', display: 'flex', gap: 16 }}>
                <a href="#" onClick={(e) => { e.preventDefault(); setActive((a) => (a + 1) % stages.length); }} style={{ color: '#ef7a7f', textDecoration: 'underline', fontSize: '0.85rem', fontWeight: 600 }}>
                  See next step →
                </a>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
                <a href="#" onClick={(e) => { e.preventDefault(); setActive((a) => (a - 1 + stages.length) % stages.length); }} style={{ color: '#8a96bf', textDecoration: 'underline', fontSize: '0.85rem', fontWeight: 500 }}>
                  Back
                </a>
              </div>
            </div>
          </div>

          {/* Visual path summary strip for the whole journey */}
          <div style={{ marginTop: 48, background: '#fbfaf7', borderRadius: 16, padding: 28, border: '1px solid rgba(26,40,73,0.06)' }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6e6e6a', marginBottom: 16 }}>Full journey at a glance</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
              {stages.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={() => setActive(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: i === active ? '#fff' : 'rgba(255,255,255,0.7)',
                      border: `1.5px solid ${i === active ? s.pinColor : 'rgba(26,40,73,0.15)'}`,
                      borderRadius: 999,
                      padding: '6px 14px',
                      color: i === active ? s.pinColor : '#3a3a37',
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      boxShadow: i === active ? '0 3px 10px rgba(26,40,73,0.12)' : 'none',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', background: s.pinColor,
                      boxShadow: `0 0 0 2.5px ${s.pinColor}22`,
                    }} />
                    {s.label}
                  </button>
                  {i < stages.length - 1 && (
                    <span style={{ color: '#d8232a', fontWeight: 700, fontSize: '1rem', margin: '0 -2px' }} aria-hidden>→</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
